import { motion } from 'framer-motion';
import { useSessionReview, formatSessionStatus } from '../../lib/useSessionReview';
import { CommentComposer } from '../comments/CommentComposer';
import { CommentList } from '../comments/CommentList';
import { MediaGallery } from '../session/MediaGallery';
import { MarkdownText } from '../MarkdownText';
import { Button } from '../ui/Button';
import { Panel } from '../ui/Panel';
import { CARDS, TEXT, ALERTS } from '../../styles/classNames';

interface SessionReviewPanelProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
}

export function SessionReviewPanel({
  isOpen,
  onClose,
  sessionId,
}: SessionReviewPanelProps) {
  const {
    session,
    sessionItems,
    comments,
    commentText,
    setCommentText,
    commentItemId,
    setCommentItemId,
    error,
    clientName,
    commentSectionRef,
    handleCommentOnThis,
    addComment,
    itemLookup,
  } = useSessionReview({ sessionId, enabled: isOpen });

  if (error) {
    return (
      <Panel isOpen={isOpen} onClose={onClose} title="Session Error" variant="modal" size="lg">
        <div className="text-center space-y-4">
          <div className={ALERTS.error}>{error}</div>
          <Button onClick={onClose} variant="secondary">
            Close
          </Button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      isOpen={isOpen}
      onClose={onClose}
      title={session?.workoutSnapshot.name || 'Session Review'}
      variant="modal"
      size="xl"
    >
      <div className="space-y-6">
        {/* Session Header Info */}
        <div>
          <p className={TEXT.small}>
            Client: {clientName || 'Loading...'} · Status: {session?.status ? formatSessionStatus(session.status) : '-'}
          </p>
        </div>

        {/* Exercise Items */}
        <div className="space-y-4">
          {sessionItems.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`${CARDS.nested} space-y-3`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className={TEXT.h3}>{item.exerciseName}</p>
                  <MarkdownText
                    content={item.exerciseDescription}
                    className={TEXT.small}
                  />
                  <MarkdownText
                    content={item.customCues || item.defaultCues}
                    className={TEXT.body}
                  />
                </div>
                <p className={`${TEXT.muted} shrink-0`}>
                  {item.setsPlanned} x {item.repsPlanned} · {item.restSecPlanned || 0}s
                </p>
              </div>

              {item.media && item.media.length > 0 && <MediaGallery media={item.media} />}

              {item.sets.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-2">
                  {item.sets.map((set) => (
                    <div
                      key={set.index}
                      className="rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-3 flex items-center justify-between"
                    >
                      <span className={TEXT.muted}>Set {set.index}</span>
                      <span className={TEXT.body}>
                        Reps: {set.repsDone ?? '-'} · Wt: {set.weight ?? '-'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button
                  variant={commentItemId === item.id ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => handleCommentOnThis(item.id)}
                >
                  {commentItemId === item.id ? 'Replying here' : 'Comment on this'}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Comments Section */}
        <motion.div
          ref={commentSectionRef}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${CARDS.nested} space-y-3`}
        >
          <p className={TEXT.h2}>Comments ({comments.length})</p>
          <CommentComposer
            commentText={commentText}
            onChange={setCommentText}
            onSubmit={addComment}
            items={sessionItems}
            targetItemId={commentItemId}
            onChangeTarget={setCommentItemId}
          />
          <CommentList
            comments={comments}
            itemLookup={itemLookup}
          />
        </motion.div>
      </div>
    </Panel>
  );
}
